import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, resolveComponent as _resolveComponent, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("p", { class: "font-bold text-start" }, "Criar convite", -1)
const _hoisted_2 = { class: "flex flex-col gap-4" }
const _hoisted_3 = { class: "flex flex-col sm:flex-row gap-4" }
const _hoisted_4 = { class: "sm:flex-1" }
const _hoisted_5 = { class: "w-full sm:w-40" }
const _hoisted_6 = {
  key: 0,
  "data-testid": "invite-crm-conflict",
  class: "rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-start flex flex-col gap-2"
}
const _hoisted_7 = { class: "text-gray-800" }
const _hoisted_8 = { class: "font-medium" }
const _hoisted_9 = { class: "flex flex-col md:flex-row md:justify-end gap-2" }
const _hoisted_10 = { class: "w-full md:max-w-[140px] order-2 md:order-1" }
const _hoisted_11 = { class: "w-full md:max-w-[140px] order-1 md:order-2" }

import { reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import StatesFromBR from '@/config/StatesFromBR'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import { storeToRefs } from 'pinia'
import { useCompanyStore } from '@/pinia-store/useCompanyStore'
import Network from '@/Network'
import { ValidationError } from 'yup'
import { createInviteSchema } from './schemas'
import {
  getInviteCrmConflict,
  getInviteErrorMessage,
  type InviteCrmConflict
} from '../../helpers/inviteErrors'

import { useToast } from 'vue-toastification'


export default /*#__PURE__*/_defineComponent({
  __name: 'CreateInviteModal',
  props: {
    modalStateProp: { type: Boolean },
    updateListFunction: { type: Function }
  },
  emits: ['close'],
  setup(__props: any, { emit }) {

const props = __props;





const form = reactive({
  companyIds: [] as (string | number)[],
  crm: '',
  crmUf: '',
  autoApprove: false
})

const errors = reactive<{ crm?: string; crmUf?: string; companyIds?: string }>({})

const loading = ref(false)
const crmConflict = ref<InviteCrmConflict | null>(null)
const { listCompanies } = storeToRefs(useCompanyStore())
const toast = useToast()
const router = useRouter()

const clearError = (field: keyof typeof errors) => {
  errors[field] = undefined
  crmConflict.value = null
}

/**
 * CTA do 409: navega para a página do médico encontrado com o CRM digitado na
 * query — o Detail abre o TransferCrmModal pré-preenchido (deep link).
 */
const goToTransferCrm = () => {
  const conflict = crmConflict.value
  if (!conflict) return
  router.push({
    path: `/medicos/${conflict.existingDoctorId}`,
    query: {
      transferCrmNumber: conflict.requestedCrm.number,
      transferCrmUf: conflict.requestedCrm.uf
    }
  })
  close()
}

const validateForm = async () => {
  errors.crm = undefined
  errors.crmUf = undefined
  errors.companyIds = undefined

  try {
    await createInviteSchema.validate(form, { abortEarly: false })
    return true
  } catch (validationError) {
    if (validationError instanceof ValidationError) {
      validationError.inner.forEach((issue) => {
        const field = issue.path as keyof typeof errors
        if (field && !errors[field]) {
          errors[field] = issue.message
        }
      })
    }
    return false
  }
}

const submitCreateInvite = async () => {
  if (!(await validateForm())) return

  try {
    loading.value = true
    const sanitizedCrm = form.crm.replace(/\s+/g, '')
    const numericCompanyIds = form.companyIds.map((id) => Number(id))

    await Network.company.inviteDoctorForCompany({
      crm: sanitizedCrm,
      crmUf: form.crmUf,
      autoApprove: form.autoApprove,
      companyIds: numericCompanyIds
    })

    toast.success('Médico(s) vinculado(s) com sucesso')
    close()

    if (props.updateListFunction) {
      try {
        await props.updateListFunction()
      } catch (error) {
        console.error('Erro ao atualizar a lista:', error)
      }
    }
  } catch (error) {
    // 409 do guardrail: alerta inline com CTA de transferência, sem toast
    const conflict = getInviteCrmConflict(error)
    if (conflict) {
      crmConflict.value = conflict
      return
    }
    // Guard de domínio (convite já ativo → mensagem curada): exibido direto,
    // pois como fallback de notifyError a mensagem curada nunca apareceria.
    toast.error(
      getInviteErrorMessage(
        error,
        'Não foi possível vincular o médico. Verifique os dados e tente novamente.'
      )
    )
    console.error('Erro ao criar convite:', error)
  } finally {
    loading.value = false
  }
}

const clear = () => {
  form.crm = ''
  form.crmUf = ''
  form.companyIds = []
  errors.crm = undefined
  errors.crmUf = undefined
  errors.companyIds = undefined
  crmConflict.value = null
}

const close = () => {
  clear()
  emit('close')
}

return (_ctx: any,_cache: any) => {
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!

  return (_openBlock(), _createBlock(ModalVue, {
    onClose: close,
    modalActive: _ctx.modalStateProp,
    hasFooterBorder: true,
    hasHeaderBorder: true,
    maxWidth: "max-w-lg"
  }, {
    header: _withCtx(() => [
      _hoisted_1
    ]),
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_9, [
        _createElementVNode("div", _hoisted_10, [
          _createVNode(OutlineButton, {
            onClick: close,
            text: "Cancelar",
            width: "100%",
            "data-testid": "invite-cancel-button"
          })
        ]),
        _createElementVNode("div", _hoisted_11, [
          _createVNode(FilledButton, {
            loading: loading.value,
            onClick: submitCreateInvite,
            text: "Criar convite",
            width: "100%",
            "data-testid": "invite-submit-button"
          }, null, 8, ["loading"])
        ])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createVNode(GenericInput, {
              modelValue: form.crm,
              "onUpdate:modelValue": [
                _cache[0] || (_cache[0] = ($event: any) => ((form.crm) = $event)),
                _cache[1] || (_cache[1] = ($event: any) => (clearError('crm')))
              ],
              label: "CRM",
              width: "100%",
              "error-messages": errors.crm,
              "data-testid": "invite-crm-input"
            }, null, 8, ["modelValue", "error-messages"])
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_component_v_autocomplete, {
              modelValue: form.crmUf,
              "onUpdate:modelValue": [
                _cache[2] || (_cache[2] = ($event: any) => ((form.crmUf) = $event)),
                _cache[3] || (_cache[3] = ($event: any) => (clearError('crmUf')))
              ],
              label: "CRMUF",
              items: _unref(StatesFromBR),
              placeholder: "Selecione",
              variant: "outlined",
              "error-messages": errors.crmUf,
              "data-testid": "invite-crmuf-select"
            }, null, 8, ["modelValue", "items", "error-messages"])
          ])
        ]),
        _createVNode(_component_v_autocomplete, {
          modelValue: form.companyIds,
          "onUpdate:modelValue": [
            _cache[4] || (_cache[4] = ($event: any) => ((form.companyIds) = $event)),
            _cache[5] || (_cache[5] = ($event: any) => (clearError('companyIds')))
          ],
          color: "primary20",
          label: "Empresas",
          items: _unref(listCompanies),
          variant: "outlined",
          multiple: "",
          chips: "",
          "error-messages": errors.companyIds,
          "data-testid": "invite-company-select"
        }, null, 8, ["modelValue", "items", "error-messages"]),
        (crmConflict.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createElementVNode("p", _hoisted_7, [
                _createTextVNode(" O CRM " + _toDisplayString(crmConflict.value.requestedCrm.number) + "/" + _toDisplayString(crmConflict.value.requestedCrm.uf) + " pertence a ", 1),
                _createElementVNode("span", _hoisted_8, _toDisplayString(crmConflict.value.cfmName), 1),
                _createTextVNode(", que já possui cadastro (CRM " + _toDisplayString(crmConflict.value.existingCrm.number) + "/" + _toDisplayString(crmConflict.value.existingCrm.uf) + "). Em vez de um novo convite, transfira o CRM para o cadastro existente. ", 1)
              ]),
              _createElementVNode("button", {
                "data-testid": "invite-transfer-crm-cta",
                class: "self-start text-primary hover:underline text-sm font-medium",
                onClick: goToTransferCrm
              }, " Transferir CRM para este cadastro → ")
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["modalActive"]))
}
}

})